// ---------------------------------------------------------------------------
// Local types (standalone — no imports from @repo/share-log-interfaces)
// ---------------------------------------------------------------------------

export interface CreateTicketPayload {
  title: string;
  description: string;
  userCategoryId?: number;
  reporterEmail?: string;
  reporterMeta?: Record<string, unknown>;
  browserInfo?: Record<string, unknown>;
  pageUrl?: string;
}

export interface TicketResponse {
  id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
}

export interface ScreenshotResponse {
  id: string;
  ticketId: string;
  url: string;
  createdAt: string;
}

export interface CategoryResponse {
  id: number;
  name: string;
  description: string | null;
  sortOrder: number;
}

interface ApiEnvelope<T> {
  message: string;
  data: T;
}

// ---------------------------------------------------------------------------
// Error
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export class ApiClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;

  constructor(apiUrl: string, apiKey: string) {
    this.baseUrl = apiUrl.replace(/\/+$/, "");
    this.apiKey = apiKey;
  }

  // ---- public methods -----------------------------------------------------

  async submitTicket(dto: CreateTicketPayload): Promise<TicketResponse> {
    return this.request<TicketResponse>("POST", "/api/v1/tickets", dto);
  }

  async uploadScreenshot(
    ticketId: string,
    file: File,
  ): Promise<ScreenshotResponse> {
    const formData = new FormData();
    formData.append("screenshot", file);

    return this.request<ScreenshotResponse>(
      "POST",
      `/api/v1/tickets/${encodeURIComponent(ticketId)}/screenshots`,
      formData,
    );
  }

  async getCategories(): Promise<CategoryResponse[]> {
    return this.request<CategoryResponse[]>("GET", "/api/v1/categories");
  }

  // ---- internals ----------------------------------------------------------

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;

    const headers: Record<string, string> = {
      "X-API-Key": this.apiKey,
    };

    const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

    if (!isFormData && body !== undefined) {
      headers["Content-Type"] = "application/json";
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: isFormData
          ? (body as FormData)
          : body !== undefined
            ? JSON.stringify(body)
            : undefined,
      });
    } catch (err) {
      throw new ApiError(
        `Network error: ${err instanceof Error ? err.message : String(err)}`,
        0,
      );
    }

    if (!response.ok) {
      let responseBody: unknown;
      try {
        responseBody = await response.json();
      } catch {
        responseBody = await response.text().catch(() => null);
      }
      throw new ApiError(
        `Request failed with status ${response.status}`,
        response.status,
        responseBody,
      );
    }

    const envelope: ApiEnvelope<T> = await response.json();
    return envelope.data;
  }
}
